import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useViewMode, type ViewMode } from '../hooks/useViewMode';
import { useCertificateSummary } from '../features/dashboard/hooks/useCertificateSummary';
import type { Client } from '../features/dashboard/types/dashboard';
import { formatUsPhone } from '../utilities/formatPhone';

type MobileClientBannerProps = {
  client: Client;
};

export default function MobileClientBanner({ client }: MobileClientBannerProps) {
  const [expanded, setExpanded] = React.useState(false);
  const { viewMode, setViewMode } = useViewMode();
  const navigate = useNavigate();
  const { summary } = useCertificateSummary(client.id);

  const handleViewChange = (mode: ViewMode) => {
    if (mode === viewMode) return;
    setViewMode(mode);
    navigate(mode === 'agent' ? '/agentdashboard' : '/insureddashboard');
  };

  return (
    <div className="mcb">
      {/* Collapsed header bar */}
      <button
        type="button"
        className="mcb__header"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <span className="mcb__iconWrap">
          <i className="pi pi-building" />
        </span>
        <div className="mcb__headerText">
          <span className="mcb__name">{client.name}</span>
          <span className="mcb__type">{client.typeLabel}</span>
        </div>
        <span className="mcb__tag">Active</span>
        <i className={expanded ? 'pi pi-chevron-up mcb__chevron' : 'pi pi-chevron-down mcb__chevron'} />
      </button>

      {expanded && (
        <div className="mcb__body">
          {/* View Toggle */}
          <div className="mcb__toggle">
            <button
              type="button"
              className={viewMode === 'agent' ? 'mcb__toggleBtn mcb__toggleBtn--active' : 'mcb__toggleBtn'}
              onClick={() => handleViewChange('agent')}
            >
              <i className="pi pi-eye" /> Agent View
            </button>
            <button
              type="button"
              className={viewMode === 'insured' ? 'mcb__toggleBtn mcb__toggleBtn--active' : 'mcb__toggleBtn'}
              onClick={() => handleViewChange('insured')}
            >
              <i className="pi pi-user" /> Insured View
            </button>
          </div>

          {/* Client Details Section */}
          <div className="mcb__section">
            <div className="mcb__sectionLabel">
              <i className="pi pi-id-card" />
              Client Details
            </div>
            <div className="mcb__details">
              <div className="mcb__detailRow">
                <i className="pi pi-user" />
                <span className="mcb__detailLabel">Contact</span>
                <span className="mcb__detailValue">{client.primaryContactName}</span>
              </div>
              {client.phone && (
                <div className="mcb__detailRow">
                  <i className="pi pi-phone" />
                  <span className="mcb__detailLabel">Phone</span>
                  <span className="mcb__detailValue">{formatUsPhone(client.phone)}</span>
                </div>
              )}
              {client.email && (
                <div className="mcb__detailRow">
                  <i className="pi pi-envelope" />
                  <span className="mcb__detailLabel">Email</span>
                  <span className="mcb__detailValue mcb__detailValue--link">{client.email}</span>
                </div>
              )}
              {client.addressLine1 && (
                <div className="mcb__detailRow">
                  <i className="pi pi-map-marker" />
                  <span className="mcb__detailLabel">Address</span>
                  <span className="mcb__detailValue">
                    {client.addressLine1}
                    {client.addressLine2 && <span className="mcb__detailSub">{client.addressLine2}</span>}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Certificate Summary Section */}
          {summary && (
            <div className="mcb__section">
              <div className="mcb__sectionLabel">
                <i className="pi pi-file" />
                Certificate Summary
              </div>
              <div className="mcb__stats">
                <div className="mcb__stat mcb__stat--success">
                  <div className="mcb__statValue">{summary.active}</div>
                  <div className="mcb__statLabel">Active</div>
                </div>
                <div className="mcb__stat mcb__stat--warning">
                  <div className="mcb__statValue">{summary.pending}</div>
                  <div className="mcb__statLabel">Pending</div>
                </div>
                <div className="mcb__stat mcb__stat--primary">
                  <div className="mcb__statValue">{summary.total}</div>
                  <div className="mcb__statLabel">Total</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
